import { useEffect, useMemo, useState } from 'react';
import { Button, Collapse, Row, Col, Form, InputGroup } from 'react-bootstrap';
import cx from 'classnames';

import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import CompareIcon from '../../../CompareIcon';
import Style from '../../index.module.css';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { ChangeRequestValidator } from 'pages/QA/components/QACardDetails/ChangeRequestValidator';
import { CompareStatus, qaCompareSimpleValue } from 'pages/QA/components/QACardDetails/utils';

interface Props {
  // Telefon
  telefon: string;
  telefonFromCr: string;
  onTelefonChange: (val: string) => void;
  telefonError?: string;

  // Epost
  epost: string | undefined;
  epostFromCr: string;
  onEpostChange: (val: string) => void;
  epostError?: string;

  // Faktura epost
  fakturaEpost: string | undefined;
  fakturaEpostFromCr: string;
  onFakturaEpostChange: (val: string) => void;
  fakturaEpostError?: string;

  // Homepage (optional)
  homepage?: string | undefined;
  homepageFromCr?: string | undefined;
  onHomepageChange?: (val: string) => void;
  homepageError?: string;

  // Daglig leder (optional)
  dagligLeder?: string | undefined;
  dagligLederFromCr?: string | undefined;
  onDagligLederChange?: (val: string) => void;
  dagligLederError?: string;
}

const ContactInfoRow = (props: Props) => {
  // Expansion toggles when any section differs or is new or has invalid CR value
  const [isExpandRow, setIsExpandRow] = useState<boolean>(false);

  // Compare statuses
  const [telefonStatus, setTelefonStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(props.telefonFromCr, props.telefon),
  );
  const [epostStatus, setEpostStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(props.epostFromCr, props.epost),
  );
  const [fakturaEpostStatus, setFakturaEpostStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(props.fakturaEpostFromCr, props.fakturaEpost),
  );
  const [homepageStatus, setHomepageStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(props.homepageFromCr as any, props.homepage as any),
  );
  const [dagligLederStatus, setDagligLederStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(props.dagligLederFromCr as any, props.dagligLeder as any),
  );

  // CR value validation errors
  const [telefonCrError, setTelefonCrError] = useState<string | null>(null);
  const [epostCrError, setEpostCrError] = useState<string | null>(null);
  const [fakturaEpostCrError, setFakturaEpostCrError] = useState<string | null>(null);
  const [homepageCrError, setHomepageCrError] = useState<string | null>(null);
  const [dagligLederCrError, setDagligLederCrError] = useState<string | null>(null);

  // derive an overall status for the header icon
  const headerStatus: CompareStatus = useMemo(() => {
    if (telefonCrError || epostCrError || fakturaEpostCrError) return 'ugyldig';
    if (homepageCrError || dagligLederCrError) return 'ugyldig';
    const statuses = [
      telefonStatus,
      epostStatus,
      fakturaEpostStatus,
      homepageStatus,
      dagligLederStatus,
    ];
    if (statuses.some((s) => s === 'ulik')) return 'ulik';
    if (statuses.some((s) => s === 'ny')) return 'ny';
    return 'lik';
  }, [
    telefonStatus,
    epostStatus,
    fakturaEpostStatus,
    homepageStatus,
    dagligLederStatus,
    telefonCrError,
    epostCrError,
    fakturaEpostCrError,
    homepageCrError,
    dagligLederCrError,
  ]);

  // auto-expand when any section differs/new/invalid
  useEffect(() => {
    if (headerStatus === 'ugyldig' || headerStatus === 'ulik' || headerStatus === 'ny') {
      setIsExpandRow(true);
    }
  }, [headerStatus]);

  // keep compare statuses in sync
  useEffect(() => {
    setTelefonStatus(qaCompareSimpleValue(props.telefonFromCr, props.telefon));
  }, [props.telefon, props.telefonFromCr]);
  useEffect(() => {
    setEpostStatus(qaCompareSimpleValue(props.epostFromCr, props.epost));
  }, [props.epost, props.epostFromCr]);
  useEffect(() => {
    setFakturaEpostStatus(qaCompareSimpleValue(props.fakturaEpostFromCr, props.fakturaEpost));
  }, [props.fakturaEpost, props.fakturaEpostFromCr]);
  useEffect(() => {
    if (props.homepageFromCr !== undefined || props.homepage !== undefined) {
      setHomepageStatus(qaCompareSimpleValue(props.homepageFromCr as any, props.homepage as any));
    }
  }, [props.homepage, props.homepageFromCr]);
  useEffect(() => {
    if (props.dagligLederFromCr !== undefined || props.dagligLeder !== undefined) {
      setDagligLederStatus(
        qaCompareSimpleValue(props.dagligLederFromCr as any, props.dagligLeder as any),
      );
    }
  }, [props.dagligLeder, props.dagligLederFromCr]);

  // validate CR values
  useEffect(() => {
    setTelefonCrError(
      props.telefonFromCr ? ChangeRequestValidator.hasTelephoneErrors(props.telefonFromCr) : null,
    );
  }, [props.telefonFromCr]);
  useEffect(() => {
    setEpostCrError(
      props.epostFromCr ? ChangeRequestValidator.hasEpostErrors(props.epostFromCr) : null,
    );
  }, [props.epostFromCr]);
  useEffect(() => {
    setFakturaEpostCrError(
      props.fakturaEpostFromCr
        ? ChangeRequestValidator.hasFakturaEpostErrors(props.fakturaEpostFromCr)
        : null,
    );
  }, [props.fakturaEpostFromCr]);
  useEffect(() => {
    if (props.homepageFromCr !== undefined) {
      setHomepageCrError(
        props.homepageFromCr
          ? ChangeRequestValidator.hasHomepageErrors(props.homepageFromCr)
          : null,
      );
    } else {
      setHomepageCrError(null);
    }
  }, [props.homepageFromCr]);
  useEffect(() => {
    // Reuse simple rule: require non-empty if provided; add more validation if needed later
    if (props.dagligLederFromCr !== undefined) {
      setDagligLederCrError(null);
    } else {
      setDagligLederCrError(null);
    }
  }, [props.dagligLederFromCr]);

  return (
    <>
      <div
        className={cx(Style.changeRequestRowHeaderContainer, {
          [Style.isNotExpandedRowContainer]: !isExpandRow,
        })}
        onClick={() => setIsExpandRow(!isExpandRow)}
      >
        <h6 className={Style.changeRequestRowHeader}>
          {!isExpandRow && <CompareIcon compareStatus={headerStatus} size="sm" />}
          <span className={Style.changeRequestRowHeaderText}>Kontaktinformasjon</span>
        </h6>
        <Button variant="link" size="sm">
          <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
        </Button>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          {/* Telefon */}
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label className="small text-muted">Telefonnummer</Form.Label>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    readOnly
                    value={props.telefonFromCr ?? ''}
                    isValid={!telefonCrError && !!props.telefonFromCr}
                    isInvalid={!!telefonCrError}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon val={props.telefonFromCr} />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {telefonCrError ?? 'Telefonnummeret er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!!telefonCrError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={telefonCrError} />
              ) : (
                <CompareIcon
                  compareStatus={telefonStatus}
                  onClick={() => props.onTelefonChange(props.telefonFromCr)}
                />
              )}
            </Col>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label className="small text-muted">Telefonnummer</Form.Label>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    value={props.telefon || ''}
                    onChange={(e) => props.onTelefonChange(e.target.value)}
                    isInvalid={!!props.telefonError}
                    isValid={!props.telefonError}
                  />
                  <Form.Control.Feedback type="invalid">{props.telefonError}</Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          {/* Epost */}
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label className="small text-muted">E-post</Form.Label>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    value={props.epostFromCr || ''}
                    readOnly
                    isValid={!!props.epostFromCr && !epostCrError}
                    isInvalid={!!epostCrError}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon val={props.epostFromCr} />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {epostCrError ?? 'Epost er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!!epostCrError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={epostCrError} />
              ) : (
                <CompareIcon
                  compareStatus={epostStatus}
                  onClick={() => props.onEpostChange(props.epostFromCr)}
                />
              )}
            </Col>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label className="small text-muted">E-post</Form.Label>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    type="text"
                    value={props.epost ?? ''}
                    onChange={(e) => props.onEpostChange(e.target.value.trim())}
                  />
                </InputGroup>
                {props.epostError && (
                  <Form.Text className="text-danger">{props.epostError}</Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Faktura Epost */}
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label className="small text-muted">Faktura E-post</Form.Label>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    value={props.fakturaEpostFromCr ?? ''}
                    readOnly
                    isValid={!!props.fakturaEpostFromCr && !fakturaEpostCrError}
                    isInvalid={!!fakturaEpostCrError}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon val={props.fakturaEpostFromCr} />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {fakturaEpostCrError ?? 'Fakturaepost er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!!fakturaEpostCrError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={fakturaEpostCrError} />
              ) : (
                <CompareIcon
                  compareStatus={fakturaEpostStatus}
                  onClick={() => props.onFakturaEpostChange(props.fakturaEpostFromCr)}
                />
              )}
            </Col>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label className="small text-muted">Faktura E-post</Form.Label>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    type="text"
                    value={props.fakturaEpost ?? ''}
                    onChange={(e) => props.onFakturaEpostChange(e.target.value.trim())}
                  />
                </InputGroup>
                {props.fakturaEpostError && (
                  <Form.Text className="text-danger">{props.fakturaEpostError}</Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Homepage - optional */}
          {(props.onHomepageChange ||
            props.homepageFromCr !== undefined ||
            props.homepage !== undefined) && (
            <Row className={Style.formGroupRow}>
              <Col xs={12} md={5}>
                <Form.Group className={Style.formGroup}>
                  <Form.Label className="small text-muted">Hjemmeside</Form.Label>
                  <InputGroup size="sm" className={Style.inputGroup}>
                    <Form.Control
                      readOnly
                      value={(props.homepageFromCr as string) || ''}
                      isValid={!!props.homepageFromCr && !homepageCrError}
                      isInvalid={!!homepageCrError}
                    />
                    <InputGroup.Text>
                      <CopyToClipboardIcon val={props.homepageFromCr as string} />
                    </InputGroup.Text>
                    <Form.Control.Feedback type="invalid">
                      {homepageCrError ?? 'URL er ugyldig.'}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs="auto" md={2} className={Style.compareIconContainer}>
                {!!homepageCrError ? (
                  <CompareIcon compareStatus="ugyldig" tooltipText={homepageCrError} />
                ) : (
                  <CompareIcon
                    compareStatus={homepageStatus}
                    onClick={() =>
                      props.onHomepageChange &&
                      props.onHomepageChange(props.homepageFromCr as string)
                    }
                  />
                )}
              </Col>
              <Col xs={12} md={5}>
                <Form.Group className={Style.formGroup}>
                  <Form.Label className="small text-muted">Hjemmeside</Form.Label>
                  <InputGroup size="sm" className={Style.inputGroup}>
                    <Form.Control
                      type="text"
                      value={props.homepage ?? ''}
                      onChange={(e) =>
                        props.onHomepageChange && props.onHomepageChange(e.target.value.trim())
                      }
                    />
                  </InputGroup>
                  {props.homepageError && (
                    <Form.Text className="text-danger">{props.homepageError}</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Daglig leder - optional */}
          {(props.onDagligLederChange ||
            props.dagligLederFromCr !== undefined ||
            props.dagligLeder !== undefined) && (
            <Row className={Style.formGroupRow}>
              <Col xs={12} md={5}>
                <Form.Group className={Style.formGroup}>
                  <Form.Label className="small text-muted">Daglig leder</Form.Label>
                  <InputGroup size="sm" className={Style.inputGroup}>
                    <Form.Control
                      readOnly
                      value={(props.dagligLederFromCr as string) || ''}
                      isValid={(props.dagligLederFromCr as string)?.length > 0}
                      isInvalid={!!dagligLederCrError}
                    />
                    <InputGroup.Text>
                      <CopyToClipboardIcon val={props.dagligLederFromCr as string} />
                    </InputGroup.Text>
                    <Form.Control.Feedback type="invalid">
                      {dagligLederCrError ?? 'Ugyldig verdi.'}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col xs="auto" md={2} className={Style.compareIconContainer}>
                {!!dagligLederCrError ? (
                  <CompareIcon compareStatus="ugyldig" tooltipText={dagligLederCrError} />
                ) : (
                  <CompareIcon
                    compareStatus={dagligLederStatus}
                    onClick={() =>
                      props.onDagligLederChange &&
                      props.onDagligLederChange(props.dagligLederFromCr as string)
                    }
                  />
                )}
              </Col>
              <Col xs={12} md={5}>
                <Form.Group className={Style.formGroup}>
                  <Form.Label className="small text-muted">Daglig leder</Form.Label>
                  <InputGroup size="sm" className={Style.inputGroup}>
                    <Form.Control
                      type="text"
                      value={props.dagligLeder ?? ''}
                      onChange={(e) =>
                        props.onDagligLederChange && props.onDagligLederChange(e.target.value)
                      }
                    />
                  </InputGroup>
                  {props.dagligLederError && (
                    <Form.Text className="text-danger">{props.dagligLederError}</Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          )}
        </div>
      </Collapse>
    </>
  );
};

export default ContactInfoRow;
